/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-restricted-globals */
/* eslint-disable react-hooks/exhaustive-deps */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabase';
import './CourseRegistration.css';

interface Course {
  id: string;
  user_id: string;
  course_name: string;
  course_code: string;
  syllabus: string;
  lecturer_keywords: string[];
  slot_index: number;
  created_at: string;
  updated_at: string;
  usage_count: number;
  is_saving?: boolean;
  last_saved?: string;
}

interface UserPlan {
  plan_id: string;
  plan_name: string;
  courses_limit: number;
  courses_created: number;
  remaining_courses: number;
}

interface ExamConfig {
  course_id: string;
  course_name: string;
  exam_type: 'mcq' | 'theory' | 'mixed';
  duration_minutes: number;
  question_count: number;
  difficulty: 'easy' | 'medium' | 'hard';
}

const CourseRegistration: React.FC = () => {
  const navigate = useNavigate();
  
  // States
  const [courses, setCourses] = useState<Course[]>([]);
  const [userPlan, setUserPlan] = useState<UserPlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [autoSaveStatus, setAutoSaveStatus] = useState<{ [key: string]: string }>({});
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showExamModal, setShowExamModal] = useState(false);
  const [editingCourse, setEditingCourse] = useState<Course | null>(null);
  
  // Form states
  const [courseName, setCourseName] = useState('');
  const [courseCode, setCourseCode] = useState('');
  const [syllabus, setSyllabus] = useState('');
  const [lecturerKeywords, setLecturerKeywords] = useState<string[]>([]);
  const [keywordInput, setKeywordInput] = useState('');
  
  // Exam config states
  const [examConfig, setExamConfig] = useState<ExamConfig>({
    course_id: '',
    course_name: '',
    exam_type: 'mcq',
    duration_minutes: 60,
    question_count: 50,
    difficulty: 'medium'
  });
  
  // Selected course for exam
  const [selectedCourseForExam, setSelectedCourseForExam] = useState<Course | null>(null);

  // Auto-save draft for new/edit course
  const [draftCourse, setDraftCourse] = useState({
    course_name: '',
    course_code: '',
    syllabus: '',
    lecturer_keywords: [] as string[]
  });

  useEffect(() => {
    loadUserData();
    loadUserCourses();
    loadDraftFromStorage();
  }, []);

  // Auto-save draft to localStorage
  useEffect(() => {
    if (showAddModal && (courseName || courseCode || syllabus || lecturerKeywords.length > 0)) {
      const draft = {
        course_name: courseName,
        course_code: courseCode,
        syllabus: syllabus,
        lecturer_keywords: lecturerKeywords
      };
      localStorage.setItem('course_draft', JSON.stringify(draft));
      setDraftCourse(draft);
    }
  }, [courseName, courseCode, syllabus, lecturerKeywords, showAddModal]);

  const loadDraftFromStorage = () => {
    const savedDraft = localStorage.getItem('course_draft');
    if (savedDraft) {
      const draft = JSON.parse(savedDraft);
      setDraftCourse(draft);
    }
  };

  const loadUserData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/login');
        return;
      }

      // Get user profile with subscription tier
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('subscription_tier, credits')
        .eq('id', user.id)
        .single();

      if (profileError) throw profileError;

      // Determine plan limits based on subscription tier
      let planId = 'free';
      let planName = 'Free Plan';
      let coursesLimit = 1;

      if (profile?.subscription_tier === 'exam_week') {
        planId = 'exam_week';
        planName = 'EXAM-WEEK PASS';
        coursesLimit = 1;
      } else if (profile?.subscription_tier === 'multi_course') {
        planId = 'multi_course';
        planName = 'MULTI-COURSE PASS';
        coursesLimit = 4;
      } else if (profile?.subscription_tier === 'semester') {
        planId = 'semester';
        planName = 'SEMESTER PASS';
        coursesLimit = 10;
      }

      setUserPlan({
        plan_id: planId,
        plan_name: planName,
        courses_limit: coursesLimit,
        courses_created: 0,
        remaining_courses: coursesLimit
      });

    } catch (err: any) {
      setError('Failed to load user data: ' + err.message);
    }
  };

  const loadUserCourses = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: courses, error: coursesError } = await supabase
        .from('courses')
        .select('*')
        .eq('user_id', user.id)
        .order('slot_index', { ascending: true });

      if (coursesError) throw coursesError;

      setCourses(courses || []);
      
      // Update user plan with actual course count
      if (userPlan) {
        setUserPlan({
          ...userPlan,
          courses_created: courses?.length || 0,
          remaining_courses: userPlan.courses_limit - (courses?.length || 0)
        });
      }

    } catch (err: any) {
      setError('Failed to load courses: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCourse = async () => {
    // Validation
    if (!courseName.trim()) {
      setError('Please enter course name');
      return;
    }
    if (!courseCode.trim()) {
      setError('Please enter course code');
      return;
    }

    // Check course limit
    if (userPlan && courses.length >= userPlan.courses_limit) {
      setError(`You have reached your course limit (${userPlan.courses_limit} courses). Please upgrade your plan to add more courses.`);
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const newCourse = {
        user_id: user.id,
        course_name: courseName.trim(),
        course_code: courseCode.trim().toUpperCase(),
        syllabus: syllabus.trim() || null,
        lecturer_keywords: lecturerKeywords,
        slot_index: courses.length + 1,
        usage_count: 0,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      const { data, error: insertError } = await supabase
        .from('courses')
        .insert([newCourse])
        .select()
        .single();

      if (insertError) throw insertError;

      // Clear draft after successful save
      localStorage.removeItem('course_draft');
      
      setSuccess(`Course "${courseName}" added successfully!`);
      resetForm();
      setShowAddModal(false);
      await loadUserCourses();

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);

    } catch (err: any) {
      setError('Failed to add course: ' + err.message);
    }
  };

  const handleEditCourse = async () => {
    if (!editingCourse) return;

    try {
      // Show saving indicator
      setAutoSaveStatus({ [editingCourse.id]: 'saving...' });
      
      const { error: updateError } = await supabase
        .from('courses')
        .update({
          course_name: courseName.trim(),
          course_code: courseCode.trim().toUpperCase(),
          syllabus: syllabus.trim() || null,
          lecturer_keywords: lecturerKeywords,
          updated_at: new Date().toISOString()
        })
        .eq('id', editingCourse.id);

      if (updateError) throw updateError;

      setAutoSaveStatus({ [editingCourse.id]: 'saved!' });
      setSuccess(`Course "${courseName}" updated successfully!`);
      
      // Clear auto-save status after 2 seconds
      setTimeout(() => {
        setAutoSaveStatus({});
      }, 2000);
      
      resetForm();
      setShowAddModal(false);
      setEditingCourse(null);
      await loadUserCourses();

      setTimeout(() => setSuccess(''), 3000);

    } catch (err: any) {
      setError('Failed to update course: ' + err.message);
      setAutoSaveStatus({});
    }
  };

  // Auto-save course changes (for editing)
  const autoSaveCourse = useCallback(async (courseId: string, updates: Partial<Course>) => {
    try {
      setAutoSaveStatus({ [courseId]: 'saving...' });
      
      const { error: updateError } = await supabase
        .from('courses')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', courseId);

      if (updateError) throw updateError;

      setAutoSaveStatus({ [courseId]: 'saved!' });
      
      setTimeout(() => {
        setAutoSaveStatus(prev => {
          const newState = { ...prev };
          delete newState[courseId];
          return newState;
        });
      }, 2000);
      
    } catch (err: any) {
      setAutoSaveStatus({ [courseId]: 'save failed' });
      console.error('Auto-save failed:', err);
    }
  }, []);

  const handleDeleteCourse = async (course: Course) => {
    if (!confirm(`Are you sure you want to delete "${course.course_name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const { error: deleteError } = await supabase
        .from('courses')
        .delete()
        .eq('id', course.id);

      if (deleteError) throw deleteError;

      setSuccess(`Course "${course.course_name}" deleted successfully!`);
      await loadUserCourses();

      // Reorder remaining courses
      const { data: remainingCourses } = await supabase
        .from('courses')
        .select('*')
        .eq('user_id', course.user_id)
        .order('created_at', { ascending: true });

      if (remainingCourses) {
        for (let i = 0; i < remainingCourses.length; i++) {
          await supabase
            .from('courses')
            .update({ slot_index: i + 1 })
            .eq('id', remainingCourses[i].id);
        }
      }

      setTimeout(() => setSuccess(''), 3000);

    } catch (err: any) {
      setError('Failed to delete course: ' + err.message);
    }
  };

  const handleStartExam = (course: Course) => {
    // Start exam independently without saving anything
    setSelectedCourseForExam(course);
    setExamConfig({
      course_id: course.id,
      course_name: course.course_name,
      exam_type: 'mcq',
      duration_minutes: 60,
      question_count: 50,
      difficulty: 'medium'
    });
    setShowExamModal(true);
  };

  const handleLaunchExam = async () => {
    if (!selectedCourseForExam) return;

    try {
      // Only increment usage count, don't save any other changes
      const { error: updateError } = await supabase
        .from('courses')
        .update({ usage_count: (selectedCourseForExam.usage_count || 0) + 1 })
        .eq('id', selectedCourseForExam.id);

      if (updateError) throw updateError;

      // Store exam config in session storage (temporary, not saved to DB)
      sessionStorage.setItem('examConfig', JSON.stringify({
        ...examConfig,
        course: selectedCourseForExam,
        started_at: new Date().toISOString()
      }));

      // Navigate to exam page - exam runs completely independent of course data
      navigate('/exam', { 
        state: { 
          examConfig,
          course: selectedCourseForExam,
          examStarted: true,
          timestamp: Date.now()
        }
      });

      // Close modal
      setShowExamModal(false);

    } catch (err: any) {
      setError('Failed to start exam: ' + err.message);
    }
  };

  const resetForm = () => {
    setCourseName('');
    setCourseCode('');
    setSyllabus('');
    setLecturerKeywords([]);
    setKeywordInput('');
  };

  const openEditModal = (course: Course) => {
    setEditingCourse(course);
    setCourseName(course.course_name);
    setCourseCode(course.course_code);
    setSyllabus(course.syllabus || '');
    setLecturerKeywords(course.lecturer_keywords || []);
    setShowAddModal(true);
  };

  const addKeyword = () => {
    if (keywordInput.trim() && !lecturerKeywords.includes(keywordInput.trim())) {
      setLecturerKeywords([...lecturerKeywords, keywordInput.trim()]);
      setKeywordInput('');
    }
  };

  const removeKeyword = (keyword: string) => {
    setLecturerKeywords(lecturerKeywords.filter(k => k !== keyword));
  };

  const restoreDraft = () => {
    if (draftCourse.course_name || draftCourse.course_code) {
      if (confirm('You have an unsaved draft. Would you like to restore it?')) {
        setCourseName(draftCourse.course_name);
        setCourseCode(draftCourse.course_code);
        setSyllabus(draftCourse.syllabus);
        setLecturerKeywords(draftCourse.lecturer_keywords);
      }
    }
  };

  if (loading) {
    return (
      <div className="course-registration-container">
        <div className="loading-wrapper">
          <div className="spinner"></div>
          <p>Loading your courses...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="course-registration-container">
      {/* Header */}
      <div className="courses-header">
        <button className="back-button" onClick={() => navigate('/dashboard')}>
          ← Back to Dashboard
        </button>
        <h1 className="courses-title">📚 My Courses</h1>
        <p className="courses-subtitle">
          Manage your courses and start practicing with AI-powered exams
        </p>
      </div>

      {/* Plan Info Card */}
      {userPlan && (
        <div className="plan-info-card">
          <div className="plan-info-header">
            <span className="plan-badge">{userPlan.plan_name}</span>
            <span className="course-limit">
              {userPlan.courses_created} / {userPlan.courses_limit} Courses Used
            </span>
          </div>
          <div className="plan-progress">
            <div 
              className="plan-progress-bar"
              style={{ width: `${(userPlan.courses_created / userPlan.courses_limit) * 100}%` }}
            />
          </div>
          {userPlan.remaining_courses === 0 && (
            <div className="upgrade-warning">
              <span>⚠️ You've reached your course limit. </span>
              <button onClick={() => navigate('/subscription')}>Upgrade Plan</button>
            </div>
          )}
        </div>
      )}

      {/* Error/Success Messages */}
      {error && (
        <div className="message-alert error">
          <span className="alert-icon">❌</span>
          <span className="alert-text">{error}</span>
          <button className="close-alert" onClick={() => setError('')}>×</button>
        </div>
      )}
      {success && (
        <div className="message-alert success">
          <span className="alert-icon">✅</span>
          <span className="alert-text">{success}</span>
          <button className="close-alert" onClick={() => setSuccess('')}>×</button>
        </div>
      )}

      {/* Add Course Button */}
      <div className="add-course-section">
        <button 
          className="add-course-btn"
          onClick={() => {
            resetForm();
            setEditingCourse(null);
            setShowAddModal(true);
            restoreDraft();
          }}
          disabled={userPlan?.remaining_courses === 0}
        >
          + Add New Course
        </button>
      </div>

      {/* Courses Grid */}
      {courses.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📖</div>
          <h3>No Courses Yet</h3>
          <p>Add your first course to start practicing with AI-powered exams</p>
          <button 
            className="empty-state-btn"
            onClick={() => setShowAddModal(true)}
          >
            Add Your First Course
          </button>
        </div>
      ) : (
        <div className="courses-grid">
          {courses.map((course, index) => (
            <div key={course.id} className="course-card">
              <div className="course-card-header">
                <div className="course-number">#{course.slot_index}</div>
                <div className="course-actions">
                  {autoSaveStatus[course.id] && (
                    <span className="auto-save-status">{autoSaveStatus[course.id]}</span>
                  )}
                  <button 
                    className="action-btn edit"
                    onClick={() => openEditModal(course)}
                    title="Edit Course"
                  >
                    ✏️
                  </button>
                  <button 
                    className="action-btn delete"
                    onClick={() => handleDeleteCourse(course)}
                    title="Delete Course"
                  >
                    🗑️
                  </button>
                </div>
              </div>
              
              <div className="course-info">
                <h3 className="course-name">{course.course_name}</h3>
                <p className="course-code">{course.course_code}</p>
                {course.syllabus && (
                  <div className="course-syllabus">
                    <strong>📋 Syllabus:</strong>
                    <p>{course.syllabus}</p>
                  </div>
                )}
                {course.lecturer_keywords && course.lecturer_keywords.length > 0 && (
                  <div className="course-keywords">
                    <strong>🔑 Lecturer Keywords:</strong>
                    <div className="keywords-list">
                      {course.lecturer_keywords.map((keyword, i) => (
                        <span key={i} className="keyword-tag">{keyword}</span>
                      ))}
                    </div>
                  </div>
                )}
                <div className="course-stats">
                  <span className="stat">
                    📊 {course.usage_count || 0} exams taken
                  </span>
                  <span className="stat">
                    📅 Added {new Date(course.created_at).toLocaleDateString()}
                  </span>
                  {course.last_saved && (
                    <span className="stat">
                      💾 Last saved {new Date(course.last_saved).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>
              
              <button 
                className="start-exam-btn"
                onClick={() => handleStartExam(course)}
              >
                🎯 Start Exam
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Course Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingCourse ? 'Edit Course' : 'Add New Course'}</h2>
              <button className="close-modal" onClick={() => setShowAddModal(false)}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label>Course Name *</label>
                <input
                  type="text"
                  value={courseName}
                  onChange={(e) => setCourseName(e.target.value)}
                  placeholder="e.g., Introduction to Computer Science"
                  className="form-input"
                />
              </div>
              
              <div className="form-group">
                <label>Course Code *</label>
                <input
                  type="text"
                  value={courseCode}
                  onChange={(e) => setCourseCode(e.target.value)}
                  placeholder="e.g., CSC 101"
                  className="form-input"
                />
              </div>
              
              <div className="form-group">
                <label>Course Outline / Syllabus</label>
                <textarea
                  value={syllabus}
                  onChange={(e) => setSyllabus(e.target.value)}
                  placeholder="Paste your course syllabus or outline here..."
                  className="form-textarea"
                  rows={4}
                />
              </div>
              
              <div className="form-group">
                <label>Lecturer's Keywords / Favorite Terms</label>
                <div className="keyword-input-group">
                  <input
                    type="text"
                    value={keywordInput}
                    onChange={(e) => setKeywordInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addKeyword()}
                    placeholder="e.g., Algorithms, Data Structures, OOP"
                    className="form-input"
                  />
                  <button type="button" onClick={addKeyword} className="add-keyword-btn">
                    Add
                  </button>
                </div>
                {lecturerKeywords.length > 0 && (
                  <div className="keywords-preview">
                    {lecturerKeywords.map((keyword, i) => (
                      <span key={i} className="keyword-badge">
                        {keyword}
                        <button onClick={() => removeKeyword(keyword)}>×</button>
                      </span>
                    ))}
                  </div>
                )}
                <small className="form-hint">
                  Add keywords your lecturer frequently uses in exams. This helps our AI generate relevant questions.
                </small>
              </div>
            </div>
            
            <div className="modal-footer">
              <button className="cancel-btn" onClick={() => setShowAddModal(false)}>
                Cancel
              </button>
              <button 
                className="submit-btn" 
                onClick={editingCourse ? handleEditCourse : handleAddCourse}
              >
                {editingCourse ? 'Update Course' : 'Add Course'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Exam Configuration Modal */}
      {showExamModal && selectedCourseForExam && (
        <div className="modal-overlay" onClick={() => setShowExamModal(false)}>
          <div className="modal-content exam-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Configure Your Exam</h2>
              <button className="close-modal" onClick={() => setShowExamModal(false)}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="selected-course-info">
                <strong>Course:</strong> {selectedCourseForExam.course_name} ({selectedCourseForExam.course_code})
              </div>
              
              <div className="form-group">
                <label>Exam Type</label>
                <select
                  value={examConfig.exam_type}
                  onChange={(e) => setExamConfig({ ...examConfig, exam_type: e.target.value as any })}
                  className="form-select"
                >
                  <option value="mcq">Multiple Choice Questions (MCQ)</option>
                  <option value="theory">Theory / Essay Questions</option>
                  <option value="mixed">Mixed (MCQ + Theory)</option>
                </select>
              </div>
              
              <div className="form-group">
                <label>Duration (minutes)</label>
                <select
                  value={examConfig.duration_minutes}
                  onChange={(e) => setExamConfig({ ...examConfig, duration_minutes: parseInt(e.target.value) })}
                  className="form-select"
                >
                  <option value={30}>30 minutes</option>
                  <option value={60}>60 minutes</option>
                  <option value={90}>90 minutes</option>
                  <option value={120}>120 minutes</option>
                  <option value={180}>180 minutes</option>
                </select>
              </div>
              
              <div className="form-group">
                <label>Number of Questions</label>
                <select
                  value={examConfig.question_count}
                  onChange={(e) => setExamConfig({ ...examConfig, question_count: parseInt(e.target.value) })}
                  className="form-select"
                >
                  <option value={20}>20 questions</option>
                  <option value={30}>30 questions</option>
                  <option value={40}>40 questions</option>
                  <option value={50}>50 questions</option>
                  <option value={75}>75 questions</option>
                  <option value={100}>100 questions</option>
                </select>
              </div>
              
              <div className="form-group">
                <label>Difficulty Level</label>
                <select
                  value={examConfig.difficulty}
                  onChange={(e) => setExamConfig({ ...examConfig, difficulty: e.target.value as any })}
                  className="form-select"
                >
                  <option value="easy">Easy - Foundational concepts</option>
                  <option value="medium">Medium - Application & analysis</option>
                  <option value="hard">Hard - Advanced & critical thinking</option>
                </select>
              </div>
              
              {selectedCourseForExam.lecturer_keywords && selectedCourseForExam.lecturer_keywords.length > 0 && (
                <div className="exam-hint">
                  <strong>💡 AI will prioritize questions based on:</strong>
                  <div className="keyword-list">
                    {selectedCourseForExam.lecturer_keywords.map((keyword, i) => (
                      <span key={i} className="keyword-pill">{keyword}</span>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="exam-independence-note">
                <p>✨ <strong>Exam Independence:</strong> Your exam will run independently without affecting saved course data.</p>
              </div>
            </div>
            
            <div className="modal-footer">
              <button className="cancel-btn" onClick={() => setShowExamModal(false)}>
                Cancel
              </button>
              <button className="submit-btn launch-exam" onClick={handleLaunchExam}>
                🚀 Launch Exam
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourseRegistration;